import { useState, useEffect } from 'react';
import './StageFormModal.css';

const COLOR_SWATCHES = [
  { name: 'Teal', value: '#004358' },
  { name: 'Sky', value: '#0090c1' },
  { name: 'Green', value: '#1aaa6e' },
  { name: 'Red', value: '#e03b3b' },
  { name: 'Gold', value: '#f0a500' },
  { name: 'Purple', value: '#6d4c9e' },
];

const EMOJI_OPTIONS = ['\u2726', '\uD83D\uDCDE', '\uD83E\uDD1D', '\uD83D\uDCCB', '\uD83D\uDCC2', '\u2699\uFE0F', '\uD83D\uDD0D', '\uD83D\uDCCA', '\u25B6', '\uD83D\uDCC8', '\u2705', '\uD83C\uDFAF', '\uD83D\uDCBC', '\uD83D\uDCC1', '\uD83D\uDD14', '\uD83D\uDCCC'];

export default function StageFormModal({ isOpen, stage, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: '', icon: '\uD83D\uDCCC', color: '#004358',
    description: '', sub_stages: [], doc_requirements: []
  });
  const [tagInput, setTagInput] = useState('');
  const [tagMode, setTagMode] = useState(null);

  useEffect(() => {
    if (stage) {
      setFormData(stage);
    } else {
      setFormData({ name: '', icon: '\uD83D\uDCCC', color: '#004358', description: '', sub_stages: [], doc_requirements: [] });
    }
    setTagInput('');
    setTagMode(null);
  }, [stage, isOpen]);

  const handleAddTag = (mode) => {
    if (tagInput.trim()) {
      setFormData({ ...formData, [mode]: [...(formData[mode] || []), tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (mode, idx) => {
    setFormData({ ...formData, [mode]: formData[mode].filter((_, i) => i !== idx) });
  };

  const handleSave = () => {
    if (!formData.name.trim()) { alert('Stage name is required'); return; }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="stage-modal-overlay" onClick={onClose}>
      <div className="stage-modal" onClick={e => e.stopPropagation()}>
        <div className="stage-modal__title">{stage ? 'Edit Stage' : 'Create New Stage'}</div>

        <div className="stage-modal__field">
          <label className="stage-modal__label">Stage Name *</label>
          <input
            type="text"
            className="stage-modal__input"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Lead Creation"
          />
        </div>

        <div className="stage-modal__field">
          <label className="stage-modal__label">Description</label>
          <textarea
            className="stage-modal__textarea"
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of this stage..."
          />
        </div>

        <div className="stage-modal__field">
          <label className="stage-modal__label">Icon</label>
          <div className="stage-modal__emoji-grid">
            {EMOJI_OPTIONS.map((emoji, i) => (
              <button
                key={i}
                className={`stage-modal__emoji-btn ${formData.icon === emoji ? 'stage-modal__emoji-btn--selected' : ''}`}
                onClick={() => setFormData({ ...formData, icon: emoji })}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        <div className="stage-modal__field">
          <label className="stage-modal__label">Color</label>
          <div className="stage-modal__color-grid">
            {COLOR_SWATCHES.map(swatch => (
              <button
                key={swatch.value}
                className={`stage-modal__color-swatch ${formData.color === swatch.value ? 'stage-modal__color-swatch--selected' : ''}`}
                style={{ background: swatch.value }}
                onClick={() => setFormData({ ...formData, color: swatch.value })}
                title={swatch.name}
              />
            ))}
          </div>
        </div>

        <div className="stage-modal__field">
          <label className="stage-modal__label">Sub-Stages</label>
          <div className="stage-modal__tags">
            {formData.sub_stages.map((ss, i) => (
              <div key={i} className="stage-modal__tag">
                {ss}
                <button className="stage-modal__tag-remove" onClick={() => handleRemoveTag('sub_stages', i)}>{'\u2715'}</button>
              </div>
            ))}
          </div>
          <div className="stage-modal__tag-row">
            <input
              type="text"
              className="stage-modal__tag-input"
              value={tagMode === 'sub_stages' ? tagInput : ''}
              onChange={e => { setTagMode('sub_stages'); setTagInput(e.target.value); }}
              placeholder="Add sub-stage..."
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag('sub_stages'); } }}
            />
            <button className="stage-modal__tag-add" onClick={() => handleAddTag('sub_stages')}>Add</button>
          </div>
        </div>

        <div className="stage-modal__field">
          <label className="stage-modal__label">Document Requirements</label>
          <div className="stage-modal__tags">
            {formData.doc_requirements.map((dr, i) => (
              <div key={i} className="stage-modal__tag">
                {dr}
                <button className="stage-modal__tag-remove" onClick={() => handleRemoveTag('doc_requirements', i)}>{'\u2715'}</button>
              </div>
            ))}
          </div>
          <div className="stage-modal__tag-row">
            <input
              type="text"
              className="stage-modal__tag-input"
              value={tagMode === 'doc_requirements' ? tagInput : ''}
              onChange={e => { setTagMode('doc_requirements'); setTagInput(e.target.value); }}
              placeholder="Add document requirement..."
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag('doc_requirements'); } }}
            />
            <button className="stage-modal__tag-add" onClick={() => handleAddTag('doc_requirements')}>Add</button>
          </div>
        </div>

        <div className="stage-modal__buttons">
          <button className="btn btn--outline" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={handleSave}>
            {stage ? 'Update Stage' : 'Create Stage'}
          </button>
        </div>
      </div>
    </div>
  );
}
